import { ErrorHandler, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from "@angular/common/http";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { RouterModule, PreloadAllModules } from "@angular/router";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { SidebarModule } from "ng-sidebar";

/*
 * Platform and Environment providers/directives/pipes
 */
import { environment } from "environments/environment";
import { ROUTES } from "./app.routes";
// App is our top level component
import { AppComponent } from "./ui";
import { APP_RESOLVER_PROVIDERS } from "./app.resolver";
import { AppState } from "./app.service";

import "../styles/styles.scss";
import "../styles/headings.css";
import { UserManager } from "./business/managers/user.manager";
import { LocalStorageRepository } from "./datastore/local/localstorage.repository";
import { RemoteRepository } from "./datastore/remote/remote.repository";
import { UserRepository } from "./datastore/repositories/user/user.repository";
import { AuthInterceptor } from "./datastore/remote/auth.interceptor";
import { ErrorHandlerHelper } from "./crossconcern/helpers/errors/errorhandler.helper";
import { BackOfficeComponent } from "./ui/backoffice";
import { GuardHelper } from "./crossconcern/helpers/generic/guard.helper";
import { FormValidatorHelper } from "./crossconcern/helpers/validators/formvalidator.helper";
import { BaseComponent } from "./ui/base/base.component";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { ErrorAlertComponent } from "./ui/components/alerts/error/error.alert.component";
import * as Sentry from "@sentry/browser";
import { SENTRY_DSN_URL, SENTRY_ENABLED } from "./crossconcern/utilities/properties/base.property";
import { UserRepositoryInterface } from "./datastore/repositories/user/user.interface";
import { RemoteRepositoryInterface } from "./datastore/remote/remote.interface";
import { LocalStorageRepositoryInterface } from "./datastore/local/localstorage.interface";
import { EmployeeManager } from "./business/managers/employee.manager";
import { EmployeeRepositoryInterface } from "./datastore/repositories/employee/employee.interface";
import { EmployeeRepository } from "./datastore/repositories/employee/employee.repository";
import { TableListComponent } from "./ui/components/tablelist/tablelist.component";
import { HttpErrorInterceptor } from "./datastore/remote/error.interceptor";
import { PhotosphereComponent } from "./ui/components/photosphere/photosphere.component";
import { PhotosphereRepositoryInterface } from "./datastore/repositories/photosphere/photosphere.interface";
import { PhotosphereRepository } from "./datastore/repositories/photosphere/photosphere.repository";
import { PhotosphereManager } from "./business/managers/photosphere.manager";
import { SidebarComponent } from "./ui/backoffice/sidebar/sidebar.component";
import { HeaderComponent } from "./ui/backoffice/header/header.component";
import { SphereEditComponent } from "./ui/backoffice/backofficemain/nursery/sphereedit/sphereedit.component";
import { PromotionRepositoryInterface } from "./datastore/repositories/promotion/promotion.interface";
import { PromotionRepository } from "./datastore/repositories/promotion/promotion.repository";
import { PromotionListComponent } from "./ui/components/promotionlist/promotionlist.component";
import { PromotionEditorComponent } from "./ui/components/promotioneditor/promotioneditor.component";
import { NgMultiSelectDropDownModule } from "ng-multiselect-dropdown";
import { ProductRepositoryInterface } from "./datastore/repositories/product/product.interface";
import { ProductRepository } from "./datastore/repositories/product/product.repository";
import { ArrowEditorComponent } from "./ui/components/arroweditor/arroweditor.component";
import { ListHeaderComponent } from "./ui/components/listheader/listheader.component";
import { NurseryListComponent } from "./ui/backoffice/backofficemain/nursery/list";
import { NurseryComponent } from "./ui/backoffice/backofficemain/nursery";
import { NurseryDetailComponent } from "./ui/backoffice/backofficemain/nursery/detail";
import { BackofficeMainComponent } from "./ui/backoffice/backofficemain";
import { FormComponent } from "./ui/components/form/form.component";
import { NgSelectModule } from "@ng-select/ng-select";
import { PaginationComponent } from "./ui/components/pagination/pagination.component";
import { NurseryManager } from "./business/managers/nursery.manager";
import { NurseryRepositoryInterface } from "./datastore/repositories/nursery/nursery.interface";
import { NurseryRepository } from "./datastore/repositories/nursery/nursery.repository";
import { NurseryEditComponent } from "./ui/backoffice/backofficemain/nursery/edit";
import { MediaRepositoryInterface } from "./datastore/repositories/media/media.interface";
import { MediaRepository } from "./datastore/repositories/media/media.repository";
import { NurseryMapComponent } from "./ui/backoffice/backofficemain/nursery/map/nurserymap.component";
import { AgmCoreModule } from "@agm/core";
import { AgmDrawingModule } from "@agm/drawing";
import { PolygonEditorComponent } from "./ui/components/polygoneditor/polygoneditor.component";
import { ShortcutListComponent } from "./ui/components/shortcutlist/shortcutlist.component";
import { NurseriesComponent } from "./ui/nurseries/nurseries.component";
import { NurseriesSphereComponent } from "./ui/nurseries/spheres/spheres.component";
import { ChangePasswordComponent } from "./ui/backoffice/changePassword";
import { PromotionComponent } from "./ui/backoffice/backofficemain/promotion/promotion.component";
import { PromotionPrivateListComponent } from "./ui/backoffice/backofficemain/promotion/list/promotionlist.component";
import { PromotionEditComponent } from "./ui/backoffice/backofficemain/promotion/edit";
import { PromotionNewComponent } from "./ui/backoffice/backofficemain/promotion/new";
import { LogComponent } from "./ui/backoffice/backofficemain/log/log.component";
import { LogManager } from "./business/managers/log.manager";
import { LogRepositoryInterface } from "./datastore/repositories/log/log.interface";
import { LogRepository } from "./datastore/repositories/log/log.repository";
import { ConfirmAlertComponent } from "./ui/components/alerts/confirm/confirm.alert.component";
import { FrontMapComponent } from "./ui/nurseries/map/frontmap.component";
import { BackofficeGuardHelper } from "./crossconcern/helpers/generic/backofficeguard.helper";
import { ProductlinkComponent } from "./ui/components/productlink/productlink.component";
import { NurseryCarouselComponent } from "./ui/components/nurserycarousel/nurserycarousel.component";
import { CarouselModule } from "ngx-owl-carousel-o";
import { LoginComponent } from "./ui/components/login/login.component";
import { FrontofficeloginComponent } from "./ui/frontofficelogin/frontofficelogin.component";
import { BackofficeloginComponent } from "./ui/backofficelogin/backofficelogin.component";
import { FrontsidebarComponent } from "./ui/nurseries/frontsidebar/frontsidebar.component";
import { FrontPhotosphereComponent } from "./ui/components/frontPhotosphere/frontphotosphere.component";
import { NurseryNewComponent } from "./ui/backoffice/backofficemain/nursery/new";

// Application wide providers
const APP_PROVIDERS = [
    ...APP_RESOLVER_PROVIDERS,
    AppState,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    { provide: ErrorHandler, useClass: ErrorHandlerHelper },
    GuardHelper,
    BackofficeGuardHelper,
    // Managers
    UserManager,
    EmployeeManager,
    PhotosphereManager,
    NurseryManager,
    LogManager,
    // Repositories
    { provide: LocalStorageRepositoryInterface, useClass: LocalStorageRepository },
    { provide: RemoteRepositoryInterface, useClass: RemoteRepository },
    { provide: UserRepositoryInterface, useClass: UserRepository },
    { provide: EmployeeRepositoryInterface, useClass: EmployeeRepository },
    { provide: PhotosphereRepositoryInterface, useClass: PhotosphereRepository },
    { provide: PromotionRepositoryInterface, useClass: PromotionRepository },
    { provide: ProductRepositoryInterface, useClass: ProductRepository },
    { provide: NurseryRepositoryInterface, useClass: NurseryRepository },
    { provide: MediaRepositoryInterface, useClass: MediaRepository },
    { provide: LogRepositoryInterface, useClass: LogRepository },
    // Helpers
    ErrorHandlerHelper,
    FormValidatorHelper
];

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
    return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

Sentry.init({
    dsn: SENTRY_DSN_URL,
    enabled: JSON.parse(SENTRY_ENABLED)
});

/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
    bootstrap: [
        AppComponent
    ],
    declarations: [
        // Components
        ChangePasswordComponent,
        AppComponent,
        BaseComponent,
        HeaderComponent,
        TableListComponent,
        PhotosphereComponent,
        FrontPhotosphereComponent,
        ListHeaderComponent,
        FormComponent,
        PaginationComponent,
        PolygonEditorComponent,
        PromotionListComponent,
        PromotionEditorComponent,
        ArrowEditorComponent,
        ShortcutListComponent,
        ProductlinkComponent,
        NurseryCarouselComponent,
        LoginComponent,
        // Alerts
        ErrorAlertComponent,
        ConfirmAlertComponent,
        // Frontoffice
        FrontofficeloginComponent,
        NurseriesComponent,
        NurseriesSphereComponent,
        FrontMapComponent,
        // Backoffice
        BackOfficeComponent,
        SidebarComponent,
        SphereEditComponent,
        BackofficeMainComponent,
        BackofficeloginComponent,
        // Nursery
        NurseryComponent,
        NurseryDetailComponent,
        NurseryListComponent,
        NurseryEditComponent,
        NurseryMapComponent,
        NurseryNewComponent,
        FrontsidebarComponent,
        // Promotion
        PromotionComponent,
        PromotionPrivateListComponent,
        PromotionEditComponent,
        PromotionNewComponent,
        // Log
        LogComponent
    ],
    entryComponents: [
        // Alerts
        ErrorAlertComponent,
        ConfirmAlertComponent
    ],
    /**
     * Import Angular's modules.
     */
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        NgbModule,
        NgMultiSelectDropDownModule.forRoot(),
        SidebarModule.forRoot(),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (createTranslateLoader),
                deps: [
                    HttpClient
                ]
            }
        }),
        RouterModule.forRoot(ROUTES, {
            useHash: Boolean(history.pushState) === false,
            preloadingStrategy: PreloadAllModules
        }),
        AgmCoreModule.forRoot({
            apiKey: "AIzaSyCNDeaphlPux42NwUqwvREKb6Wi0axjb0U", // PROD
            // apiKey: "AIzaSyBdwfjPwKpebSZ2sAR8iSKaqtdL5hWS-j8", // STAGING
            // apiKey: "AIzaSyDYb_n0mHpSkKtiY4AhuRz2M9ZSOMb2xHo", // DEV
            libraries: [
                "places", "drawing", "geometry"
            ]
        }),
        AgmDrawingModule,

        /**
         * This section will import the `DevModuleModule` only in certain build types.
         * When the module is not imported it will get tree shaked.
         * This is a simple example, a big app should probably implement some logic
         */
        ...environment.showDevModule ? [] : [],
        NgSelectModule,
        BrowserAnimationsModule,
        CarouselModule
    ],
    /**
     * Expose our Services and Providers into Angular's dependency injection.
     */
    providers: [
        environment.ENV_PROVIDERS,
        APP_PROVIDERS
    ]
})
export class AppModule {}
